import React, { useState, useEffect } from 'react';
import { ChevronDown, Instagram, Linkedin } from 'lucide-react';
import {
  sectionPadding,
  pageTopPadding,
  CONTENT_MAX_WIDE,
  CONTENT_INSET,
} from '../styles/layout';

const TikTokIcon = ({ size = 24, color = '#C9A84C' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M9 12a4 4 0 0 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

const GOLD = '#C9A84C';
const BG = '#090909';
const CARD_BG = '#111111';
const TEXT_MUTED = '#F5F0E8';
const GOLD_BORDER = '1px solid rgba(201, 168, 76, 0.2)';

const ABOUT_FAQS = [
  {
    question: 'How does the mobile golf simulator work?',
    answer:
      'We deliver a fully enclosed inflatable bay with 4K projection, premium hitting surface, and professional launch monitor to your location. Our crew sets up, calibrates the system, and breaks everything down when your event ends—you simply show up and play.',
  },
  {
    question: 'What areas do you serve?',
    answer:
      'We serve the Greater Phoenix metropolitan area within a 50-mile radius of downtown Phoenix, including Scottsdale, Tempe, Mesa, Chandler, Gilbert, and surrounding communities.',
  },
  {
    question: 'How much does it cost to book a simulator?',
    answer:
      'Pricing depends on date, duration, and add-ons. Weekend and peak dates may differ from weekday rates. Visit our booking flow or The Experience page for current packages, or contact us for a custom quote.',
  },
  {
    question: 'How far in advance do I need to book?',
    answer:
      'We recommend booking at least 2–3 weeks ahead, especially for weekends and peak season. Last-minute availability is possible depending on our calendar—check online or reach out directly.',
  },
  {
    question: 'Do I need to bring my own golf clubs?',
    answer:
      'No. We provide professional clubs for right- and left-handed players, including options suitable for beginners and juniors. You are welcome to bring your own clubs if you prefer.',
  },
  {
    question: 'Can beginners use the simulator?',
    answer:
      'Absolutely. Our simulators are beginner-friendly, with practice modes and optional on-site coaching. We make it easy for first-time players to enjoy the experience.',
  },
  {
    question: 'What types of events are best suited for the simulator?',
    answer:
      'Corporate events, birthday parties, weddings, backyard gatherings, college events, and fundraisers are all popular. Any occasion where guests want a fun, interactive activity works well.',
  },
  {
    question: 'Is there a league or competitive play option?',
    answer:
      'Yes. Visit The League for tournaments, happy hours, group play, and open sessions. We run competitive events with live rankings and community play throughout the season.',
  },
];

const SOCIAL_LINKS = [
  {
    label: 'Instagram',
    handle: '@digitaldivot',
    href: 'https://www.instagram.com/digitaldivot/',
    Icon: Instagram,
  },
  {
    label: 'TikTok',
    handle: '@digitaldivot',
    href: 'https://www.tiktok.com/@digitaldivot',
    Icon: TikTokIcon,
  },
  {
    label: 'LinkedIn',
    handle: '@digitaldivot',
    href: 'https://www.linkedin.com/company/digital-divot',
    Icon: Linkedin,
  },
];

const FOUNDERS = [
  {
    name: 'Ahmed Elmadbouly',
    role: 'Co-Founder',
    image: '/Ahmed_Elmadbouly.JPG',
    imageScale: 2.4,
    imageOrigin: '50% 22%',
    bio: 'ASU alumnus with a degree in Supply Chain Management. Passionate about bringing innovative mobile golf experiences to Arizona.',
  },
  {
    name: 'Youssef Zonfl',
    role: 'Co-Founder',
    image: '/Youssef_Zonfl.jpg',
    imageScale: 2.65,
    imagePosition: '50% 26%',
    imageTranslateY: '6%',
    bio: 'ASU alumnus with a degree in Engineering Management. Ensures every event runs smoothly with precision and innovation.',
  },
  {
    name: 'Ethan Atias',
    role: 'Co-Founder',
    image: '/Ethan_Atias.JPG',
    imageScale: 2.3,
    imageOrigin: '50% 28%',
    bio: 'ASU alumnus with a degree in Finance. Manages our financial strategy and ensures sustainable growth for Digital Divot.',
  },
];

const About = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const containerStyle = { maxWidth: CONTENT_MAX_WIDE, margin: '0 auto', width: '100%' };

  return (
    <div
      className="dd-page dd-about"
      style={{
        paddingTop: pageTopPadding(isMobile),
        backgroundColor: BG,
        width: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <section
        style={{
          padding: sectionPadding(isMobile),
          backgroundColor: BG,
          overflow: 'hidden',
        }}
      >
        <div style={{ ...containerStyle, padding: `0 ${CONTENT_INSET}` }}>
          <span
            style={{
              display: 'block',
              fontSize: '0.75rem',
              fontWeight: '700',
              letterSpacing: '0.2em',
              color: GOLD,
              marginBottom: isMobile ? '32px' : '40px',
            }}
          >
            WHO WE ARE
          </span>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: isMobile ? '40px' : '48px',
              marginBottom: isMobile ? '56px' : '72px',
              alignItems: 'start',
              overflow: 'hidden',
            }}
          >
            <div>
              <h2
                style={{
                  fontSize: isMobile ? '1.75rem' : '2.25rem',
                  fontWeight: '800',
                  color: '#F5F0E8',
                  lineHeight: 1.2,
                  marginBottom: '20px',
                }}
              >
                The Team Behind{' '}
                <span style={{ color: GOLD }}>Digital Divot</span>
              </h2>
              <p
                style={{
                  fontSize: isMobile ? '1rem' : '1.0625rem',
                  color: TEXT_MUTED,
                  lineHeight: 1.7,
                  marginBottom: 0,
                }}
              >
                We're a Phoenix-based startup on a mission to make golf accessible to everyone, with zero hassle involved.
                Tour-quality golf, delivered straight to your event. From first inquiry to final breakdown, we handle the
                logistics, technology, and guest experience ourselves, so every event feels effortless and unforgettable.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', overflow: 'hidden' }}>
              {FOUNDERS.map((founder) => (
                <div
                  key={founder.name}
                  style={{
                    backgroundColor: CARD_BG,
                    border: GOLD_BORDER,
                    borderRadius: '16px',
                    padding: '20px',
                    display: 'flex',
                    gap: '16px',
                    alignItems: 'flex-start',
                  }}
                >
                  <div
                    style={{
                      width: '56px',
                      height: '56px',
                      borderRadius: '50%',
                      backgroundColor: BG,
                      border: GOLD_BORDER,
                      flexShrink: 0,
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={founder.image}
                      alt={founder.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: founder.imagePosition ?? 'center top',
                        transform: [
                          founder.imageScale && `scale(${founder.imageScale})`,
                          founder.imageTranslateY && `translateY(${founder.imageTranslateY})`,
                        ]
                          .filter(Boolean)
                          .join(' ') || undefined,
                        transformOrigin: founder.imageOrigin ?? 'center center',
                      }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3
                      style={{
                        fontSize: '1.0625rem',
                        fontWeight: '700',
                        color: '#F5F0E8',
                        margin: '0 0 4px',
                      }}
                    >
                      {founder.name}
                    </h3>
                    <p
                      style={{
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: GOLD,
                        margin: '0 0 8px',
                      }}
                    >
                      {founder.role}
                    </p>
                    <p
                      style={{
                        fontSize: '0.875rem',
                        color: TEXT_MUTED,
                        lineHeight: 1.55,
                        margin: 0,
                      }}
                    >
                      {founder.bio}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2
              style={{
                fontSize: isMobile ? '1.75rem' : '2.25rem',
                fontWeight: '800',
                color: GOLD,
                marginBottom: '24px',
                textAlign: isMobile ? 'center' : 'left',
              }}
            >
              Frequently Asked Questions
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {ABOUT_FAQS.map((faq, index) => {
                const isOpen = openFaqIndex === index;
                return (
                  <div
                    key={faq.question}
                    style={{
                      backgroundColor: CARD_BG,
                      border: GOLD_BORDER,
                      borderRadius: '16px',
                      overflow: 'hidden',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '16px',
                        padding: isMobile ? '16px 18px' : '18px 22px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <span
                        style={{
                          fontSize: isMobile ? '0.9375rem' : '1rem',
                          fontWeight: '600',
                          color: '#F5F0E8',
                          lineHeight: 1.4,
                        }}
                      >
                        {faq.question}
                      </span>
                      <ChevronDown
                        size={22}
                        color={GOLD}
                        style={{
                          flexShrink: 0,
                          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.25s ease',
                        }}
                      />
                    </button>
                    {isOpen && (
                      <div
                        style={{
                          padding: isMobile ? '0 18px 18px' : '0 22px 22px',
                        }}
                      >
                        <p
                          style={{
                            fontSize: '0.9375rem',
                            color: TEXT_MUTED,
                            lineHeight: 1.65,
                            margin: 0,
                          }}
                        >
                          {faq.answer}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div
            style={{
              marginTop: isMobile ? '48px' : '64px',
              padding: isMobile ? '28px 20px' : '36px 40px',
              backgroundColor: CARD_BG,
              border: GOLD_BORDER,
              borderRadius: '16px',
              textAlign: 'center',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <h2
              style={{
                fontSize: isMobile ? '1.5rem' : '1.75rem',
                fontWeight: '800',
                color: GOLD,
                marginBottom: '12px',
              }}
            >
              Follow Us
            </h2>
            <p
              style={{
                fontSize: '1rem',
                color: TEXT_MUTED,
                lineHeight: 1.6,
                margin: '0 auto 28px',
                maxWidth: '28rem',
              }}
            >
              Stay in the loop — event highlights, behind-the-scenes setup, and booking updates.
            </p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile
                  ? 'repeat(2, minmax(0, 1fr))'
                  : 'repeat(3, minmax(0, 1fr))',
                gap: isMobile ? '12px' : '16px',
                width: '100%',
                maxWidth: isMobile ? '100%' : '36rem',
                margin: '0 auto',
              }}
            >
              {SOCIAL_LINKS.map(({ label, handle, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${label} ${handle}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    minWidth: 0,
                    width: '100%',
                    padding: isMobile ? '14px 12px' : '16px 12px',
                    borderRadius: '12px',
                    border: GOLD_BORDER,
                    backgroundColor: BG,
                    textDecoration: 'none',
                    color: TEXT_MUTED,
                    transition: 'border-color 0.2s ease, color 0.2s ease',
                    boxSizing: 'border-box',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(201, 168, 76, 0.5)';
                    e.currentTarget.style.color = GOLD;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(201, 168, 76, 0.2)';
                    e.currentTarget.style.color = TEXT_MUTED;
                  }}
                >
                  <Icon size={28} color={GOLD} />
                  <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#F5F0E8' }}>
                    {label}
                  </span>
                  <span style={{ fontSize: '0.8125rem' }}>{handle}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
